import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Button, Stack, Chip, IconButton, Alert, Tooltip, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemText, CircularProgress
} from '@mui/material';
import {
  PlaylistAddCheck as DiagIcon,
  Add as AddIcon,
  Visibility as ViewIcon,
  PlayArrow as ResumeIcon,
  DeleteOutline as DeleteIcon,
  RestartAlt as ResetIcon,
  ReportProblem as UrgentIcon,
  Warning as ImportantIcon,
  RemoveRedEye as WatchIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { localized, UI } from '../../data/questionnaireContent';
import { assessments } from '../../services/api';
import { formatNumber } from '../../utils/scoreColors';
import { buildAssessmentPdf } from '../../utils/assessmentPdf';
import AssessmentWizard from './AssessmentWizard';
import AssessmentReview from './AssessmentReview';

function CountBadge({ icon, value, color }) {
  if (!value) return null;
  return (
    <Chip
      size="small"
      icon={icon}
      label={value}
      sx={{ backgroundColor: `${color}1A`, color, fontWeight: 700, '& .MuiChip-icon': { color } }}
    />
  );
}

function AssessmentPanel({ siteId, buildings = [], onBeforeLaunch, onChanged }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [list, setList] = useState([]);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [resumeAssessment, setResumeAssessment] = useState(null);
  const [reviewId, setReviewId] = useState(null);
  const [toast, setToast] = useState('');
  // Fenêtre de confirmation avant ouverture du questionnaire : { mode: 'new'|'resume'|'reset', assessment, investments }
  const [launch, setLaunch] = useState(null);
  const [launching, setLaunching] = useState(false);
  const [exportingId, setExportingId] = useState(null);

  const reload = useCallback(() => {
    if (!siteId) return;
    assessments.getBySite(siteId)
      .then((res) => setList(res.data))
      .catch((e) => console.error('load assessments', e));
  }, [siteId]);

  useEffect(() => { reload(); }, [reload]);

  if (!siteId) {
    return (
      <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
        {localized(UI.saveSiteFirst, lang)}
      </Alert>
    );
  }

  const requestLaunch = async (mode, assessment = null) => {
    if (mode !== 'reset') {
      setLaunch({ mode, assessment, investments: [] });
      return;
    }
    try {
      const res = await assessments.getInvestments(assessment.id);
      setLaunch({ mode, assessment, investments: res.data });
    } catch (e) {
      console.error(e);
      setToast(localized(UI.loadInvestmentsError, lang));
    }
  };

  // Enregistre le formulaire du site, puis ouvre (ou réinitialise puis ouvre) le questionnaire
  const confirmLaunch = async () => {
    const { mode, assessment } = launch;
    setLaunching(true);
    try {
      const saved = onBeforeLaunch ? await onBeforeLaunch() : true;
      if (!saved) {
        setLaunch(null); // l'erreur d'enregistrement est affichée par le formulaire
        return;
      }

      if (mode === 'new') {
        setResumeAssessment(null);
      } else if (mode === 'resume') {
        const res = await assessments.getOne(assessment.id);
        setResumeAssessment({ id: assessment.id, batiment_id: assessment.batiment_id, answers: res.data.answers || {} });
      } else {
        await assessments.reset(assessment.id);
        reload();
        if (onChanged) await onChanged();
        setResumeAssessment({ id: assessment.id, batiment_id: assessment.batiment_id, answers: {} });
      }
      setLaunch(null);
      setWizardOpen(true);
    } catch (e) {
      console.error(e);
      setLaunch(null);
      setToast(localized(mode === 'reset' ? UI.resetError : UI.launchError, lang));
    } finally {
      setLaunching(false);
    }
  };

  const handleWizardClose = (success) => {
    setWizardOpen(false);
    setResumeAssessment(null);
    if (success) reload();
  };

  const handleAccepted = (count, skippedCount) => {
    if (onChanged) onChanged({ investmentsCreated: count > 0 });
    const messages = [localized(UI.createdToast, lang).replace('{n}', count)];
    if (skippedCount > 0) messages.push(localized(UI.skippedToast, lang).replace('{n}', skippedCount));
    setToast(messages.join(' · '));
    reload();
  };

  const handleExportPdf = async (id) => {
    setExportingId(id);
    try {
      const res = await assessments.getExport(id);
      const { doc, filename } = buildAssessmentPdf(res.data, { t, lang });
      doc.save(filename);
    } catch (e) {
      console.error(e);
      setToast(localized(UI.exportPdfError, lang));
    } finally {
      setExportingId(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(localized(UI.deleteConfirm, lang))) return;
    try {
      await assessments.delete(id);
      reload();
    } catch (e) {
      console.error(e);
    }
  };

  const investmentLabel = (inv) => {
    const thematique = inv.thematique === 'autre' && inv.thematique_autre
      ? inv.thematique_autre
      : t(`thematiques.${inv.thematique}`);
    const items = inv.item
      .map((it) => (it === 'autre' && inv.item_autre ? inv.item_autre : t(`riskItems.${it}`)))
      .join(', ');
    return items ? `${thematique} — ${items}` : thematique;
  };

  const investmentDetails = (inv) => {
    const parts = [t(`validationStatus.${inv.validation_status}`)];
    if (Number(inv.montant_investissement) > 0) {
      parts.push(`${formatNumber(inv.montant_investissement)} ${inv.devise_pays || 'EUR'}`);
    }
    return parts.join(' · ');
  };

  const scopeLabel = (a) =>
    a.batiment_id
      ? `${localized(UI.building, lang)} ${a.numero_batiment || ''}`
      : localized(UI.scopeAllSite, lang);

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2.5, mb: 3, borderRadius: 3, borderColor: 'primary.light',
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.04) 0%, rgba(118, 75, 162, 0.04) 100%)' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <DiagIcon color="primary" />
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {localized(UI.panelTitle, lang)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {localized(UI.panelSubtitle, lang)}
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon />} onClick={() => requestLaunch('new')}
          sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', fontWeight: 600 }}
        >
          {localized(UI.start, lang)}
        </Button>
      </Box>

      {list.length > 0 && (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {list.map((a) => {
            const isDraft = a.status === 'draft';
            return (
              <Box
                key={a.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.2, borderRadius: 2,
                  backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {new Date(a.started_at).toLocaleDateString('fr-FR', { timeZone: 'Europe/Paris' })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">· {scopeLabel(a)}</Typography>
                    <Chip
                      size="small"
                      label={isDraft ? localized(UI.statusDraft, lang) : localized(UI.statusCompleted, lang)}
                      color={isDraft ? 'warning' : 'success'}
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                  </Box>
                  {!isDraft && (
                    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                      <CountBadge icon={<UrgentIcon />} value={a.nb_urgent} color="#E74C3C" />
                      <CountBadge icon={<ImportantIcon />} value={a.nb_important} color="#E67E22" />
                      <CountBadge icon={<WatchIcon />} value={a.nb_surveiller} color="#B7950B" />
                    </Stack>
                  )}
                </Box>

                {isDraft ? (
                  <Tooltip title={localized(UI.resume, lang)}>
                    <IconButton size="small" color="primary" onClick={() => requestLaunch('resume', a)}>
                      <ResumeIcon />
                    </IconButton>
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip title={localized(UI.viewAnswers, lang)}>
                      <IconButton size="small" onClick={() => setReviewId(a.id)}>
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={localized(UI.exportPdf, lang)}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleExportPdf(a.id)}
                          disabled={exportingId === a.id}
                          sx={{ color: '#d32f2f' }}
                        >
                          {exportingId === a.id ? <CircularProgress size={18} /> : <PdfIcon />}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </>
                )}
                <Tooltip title={localized(UI.reset, lang)}>
                  <IconButton size="small" onClick={() => requestLaunch('reset', a)}>
                    <ResetIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title={localized(UI.delete, lang)}>
                  <IconButton size="small" onClick={() => handleDelete(a.id)}>
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      )}

      {list.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {localized(UI.noAssessments, lang)}
        </Typography>
      )}

      <AssessmentWizard
        open={wizardOpen}
        onClose={handleWizardClose}
        siteId={siteId}
        buildings={buildings}
        onProposalsAccepted={handleAccepted}
        resumeAssessment={resumeAssessment}
      />
      <Dialog open={Boolean(launch)} onClose={() => !launching && setLaunch(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {localized(launch?.mode === 'reset' ? UI.resetTitle : UI.saveBeforeTitle, lang)}
        </DialogTitle>
        <DialogContent>
          {launch?.mode === 'reset' && (
            <Typography sx={{ mb: 2 }}>{localized(UI.resetText, lang)}</Typography>
          )}
          {launch?.mode === 'reset' && launch.investments.length > 0 && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {localized(UI.resetDeleteWarning, lang).replace('{n}', launch.investments.length)}
              </Typography>
              <List dense disablePadding sx={{ mt: 1 }}>
                {launch.investments.map((inv) => (
                  <ListItem key={inv.id} disableGutters sx={{ py: 0 }}>
                    <ListItemText
                      primary={`• ${investmentLabel(inv)}`}
                      secondary={investmentDetails(inv)}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption', sx: { pl: 1.5 } }}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            {localized(UI.saveBeforeText, lang)}
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLaunch(null)} disabled={launching}>
            {localized(UI.cancel, lang)}
          </Button>
          <Button
            variant="contained"
            color={launch?.mode === 'reset' && launch.investments.length > 0 ? 'error' : 'primary'}
            onClick={confirmLaunch}
            disabled={launching}
            startIcon={launching ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {launch?.mode === 'reset' && launch.investments.length > 0
              ? localized(UI.resetDeleteButton, lang).replace('{n}', launch.investments.length)
              : launch?.mode === 'reset'
                ? localized(UI.reset, lang)
                : localized(UI.saveAndContinue, lang)}
          </Button>
        </DialogActions>
      </Dialog>
      <AssessmentReview
        open={Boolean(reviewId)}
        onClose={() => setReviewId(null)}
        assessmentId={reviewId}
      />
      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Paper>
  );
}

export default AssessmentPanel;
